/**
 * Weather Widget for TTC Subway Map
 * Displays current weather information in the header
 * Uses Open-Meteo API directly from the client
 */

import { iconFont } from './icon-fonts.js';

// Toronto coordinates
const TORONTO_LAT = 43.7064;
const TORONTO_LON = -79.3986;
const OPEN_METEO_URL = "https://api.open-meteo.com/v1/forecast";

// Cache for weather data to avoid excessive API calls
let weatherCache = null;
let lastFetchTime = 0;
const CACHE_DURATION = 30 * 60 * 1000; // 30 minutes in milliseconds

/**
 * Initialize the weather widget
 */
async function initWeatherWidget() {
  const weatherWidget = document.getElementById('weatherWidget');
  if (!weatherWidget) return;

  try {
    await updateWeatherDisplay(weatherWidget);

    // Update weather every 30 minutes
    setInterval(() => {
      updateWeatherDisplay(weatherWidget);
    }, CACHE_DURATION);
  } catch (error) {
    console.error('Failed to initialize weather widget:', error);
    weatherWidget.innerHTML = `
      <div class="flex items-center text-gray-500">
        ${iconFont('cloud_off', '')}
        <span class="ml-1 text-sm">Weather unavailable</span>
      </div>
    `;
  }
}

/**
 * Update the weather display with current data
 * @param {HTMLElement} widget - The weather widget element
 */
async function updateWeatherDisplay(widget) {
  try {
    const weatherData = await getWeatherData();
    if (!weatherData) {
      throw new Error('No weather data available');
    }

    const { current, units } = weatherData;
    const icon = getWeatherIcon(current.weathercode, current.isDay);

    const iconName = mapWeatherCodeToFontAwesome(current.weathercode, current.isDay);

    widget.innerHTML = `
      <div class="flex items-center">
        ${iconFont(iconName, 'text-2xl mr-1 text-gray-300')}
        <div>
          <div class="font-medium">${Math.round(current.temperature)}${units.temperature}</div>
          <div class="text-xs text-gray-300">Toronto</div>
        </div>
      </div>
    `;

    // Remove loading class if present
    widget.classList.remove('animate-pulse');
  } catch (error) {
    console.error('Error updating weather display:', error);
    widget.innerHTML = `
      <div class="flex items-center text-gray-500">
        ${iconFont('cloud_off', '')}
        <span class="ml-1 text-sm">Weather unavailable</span>
      </div>
    `;
  }
}

/**
 * Get weather data directly from Open-Meteo API, using cache if available and not expired
 * @returns {Promise<Object>} Weather data
 */
async function getWeatherData() {
  const now = Date.now();

  // Use cached data if available and not expired
  if (weatherCache && (now - lastFetchTime < CACHE_DURATION)) {
    return weatherCache;
  }

  try {
    // Simplified API call with only the essential parameters
    const url = `${OPEN_METEO_URL}?latitude=${TORONTO_LAT}&longitude=${TORONTO_LON}&current=temperature_2m,weather_code,is_day`;
    console.log("Fetching weather from:", url);

    // Fetch fresh weather data directly from Open-Meteo
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`Weather API error: ${response.status}`);
    }

    const data = await response.json();
    console.log("Weather data received:", data);

    // Process the data with simpler structure
    const processedData = {
      current: {
        temperature: data.current.temperature_2m,
        weathercode: data.current.weather_code,
        isDay: data.current.is_day === 1
      },
      units: {
        temperature: data.current_units?.temperature_2m || "°C"
      }
    };

    // Update cache
    weatherCache = processedData;
    lastFetchTime = now;

    return processedData;
  } catch (error) {
    console.error('Failed to fetch weather data:', error);
    return null;
  }
}

/**
 * Map weather code to Font Awesome icon name
 * @param {number} code - Weather code from API
 * @param {boolean} isDay - Whether it's daytime
 * @returns {string} Font Awesome icon name
 */
function mapWeatherCodeToFontAwesome(code, isDay) {
  // Weather codes from Open-Meteo API mapped to Font Awesome icons
  if (code === 0) { // Clear sky
    return isDay ? "sun" : "moon";
  } else if (code === 1 || code === 2) { // Mainly clear / Partly cloudy
    return isDay ? "cloud_sun" : "cloud_moon";
  } else if (code === 3) { // Overcast
    return "cloud";
  } else if ([45, 48].includes(code)) { // Fog
    return "smog";
  } else if ([51, 53, 55, 56, 57, 61, 63, 65, 66, 67, 80, 81, 82].includes(code)) { // Rain/Drizzle
    return "cloud_rain";
  } else if ([71, 73, 75, 77, 85, 86].includes(code)) { // Snow
    return "snowflake";
  } else if ([95, 96, 99].includes(code)) { // Thunderstorm
    return "cloud_bolt";
  }

  return "cloud"; // Default to cloud icon
}

/**
 * Get weather icon based on weather code and day/night (legacy function)
 * @param {number} code - Weather code from API
 * @param {boolean} isDay - Whether it's daytime
 * @returns {string} Material icon name
 */
function getWeatherIcon(code, isDay) {
  // Weather codes from Open-Meteo API
  // https://open-meteo.com/en/docs
  if (code === 0) { // Clear sky
    return isDay ? "clear_day" : "clear_night";
  } else if (code === 1) { // Mainly clear
    return isDay ? "partly_cloudy_day" : "partly_cloudy_night";
  } else if (code === 2) { // Partly cloudy
    return isDay ? "partly_cloudy_day" : "partly_cloudy_night";
  } else if (code === 3) { // Overcast
    return "cloud";
  } else if ([45, 48].includes(code)) { // Fog
    return "foggy";
  } else if ([51, 53, 55, 56, 57].includes(code)) { // Drizzle
    return "rainy";
  } else if ([61, 63, 65, 66, 67].includes(code)) { // Rain
    return "rainy";
  } else if ([71, 73, 75, 77].includes(code)) { // Snow
    return "weather_snowy";
  } else if ([80, 81, 82].includes(code)) { // Rain showers
    return "rainy";
  } else if ([85, 86].includes(code)) { // Snow showers
    return "weather_snowy";
  } else if ([95, 96, 99].includes(code)) { // Thunderstorm
    return "thunderstorm";
  }

  return "help_outline"; // Default icon if code not recognized
}

// Initialize the weather widget when the DOM is loaded
document.addEventListener('DOMContentLoaded', initWeatherWidget);